import { useState } from "react";
import { projectInput, type Project, type ProjectInput, type ProjectUpdate } from "@akhshy/types";
import type { UseResource } from "../../../lib/useResource.ts";
import { useForm } from "../../../lib/useForm.ts";
import { useSlugField } from "../../../lib/slug.ts";
import { FormField } from "../../molecules/FormField.tsx";
import { MetaFields } from "../../molecules/MetaFields.tsx";
import { SlugField } from "../../molecules/SlugField.tsx";
import { TagInput } from "../../molecules/TagInput.tsx";
import { FormActions } from "../../molecules/FormActions.tsx";
import { LinkRowsEditor, type LinkRow } from "../../molecules/RepeatableRows.tsx";
import { Input } from "../../atoms/Input.tsx";
import { Textarea } from "../../atoms/Textarea.tsx";

interface Props {
  initial: Project | null;
  onDone: () => void;
  resource: UseResource<Project, ProjectInput, ProjectUpdate>;
}

export function ProjectForm({ initial, onDone, resource }: Props) {
  const { errors, submitting, submit } = useForm(projectInput, "Project saved");
  const [tag, setTag] = useState(initial?.tag ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [markerNote, setMarkerNote] = useState(initial?.markerNote ?? "");
  const [links, setLinks] = useState<LinkRow[]>(initial?.links ?? []);
  const { slug, setSlug, suggestFrom } = useSlugField(initial?.slug ?? "");
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [published, setPublished] = useState(initial?.published ?? true);
  const [sortOrder, setSortOrder] = useState(initial?.sortOrder ?? 0);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      tag,
      name,
      description,
      markerNote: markerNote.trim() === "" ? null : markerNote,
      links,
      slug: slug || undefined,
      tags,
      published,
      sortOrder,
    };
    void submit(payload, async (parsed) => {
      if (initial) await resource.update(initial.id, parsed);
      else await resource.create(parsed);
      onDone();
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="field-row">
        <FormField label="Tag" error={errors["tag"]}>
          <Input value={tag} placeholder="Open source" onChange={(e) => setTag(e.target.value)} />
        </FormField>
        <FormField label="Name" error={errors["name"]}>
          <Input
            value={name}
            placeholder="envault"
            onChange={(e) => {
              setName(e.target.value);
              suggestFrom(e.target.value);
            }}
          />
        </FormField>
      </div>
      <SlugField slug={slug} onSlug={setSlug} error={errors["slug"]} />
      <FormField label="Description" error={errors["description"]}>
        <Textarea value={description} rows={3} onChange={(e) => setDescription(e.target.value)} />
      </FormField>
      <FormField label="Marker note (optional)" error={errors["markerNote"]}>
        <Input value={markerNote} placeholder="currently shipping — v0.5.0" onChange={(e) => setMarkerNote(e.target.value)} />
      </FormField>
      <FormField label="Links" error={errors["links"]}>
        <LinkRowsEditor
          rows={links}
          onChange={setLinks}
          labelPlaceholder="GitHub"
          urlPlaceholder="https://github.com/…"
          addLabel="Add link"
        />
      </FormField>
      <TagInput value={tags} onChange={setTags} error={errors["tags"]} />
      <MetaFields
        published={published}
        sortOrder={sortOrder}
        onPublished={setPublished}
        onSortOrder={setSortOrder}
        error={errors["sortOrder"]}
      />
      <FormActions submitting={submitting} editing={initial !== null} onCancel={onDone} />
    </form>
  );
}
